import { useEffect, useRef, useState } from "react";
import { message } from "antd";
import Service from "../service";
import { showAuthLoader, hideAuthLoader } from "../appRedux/actions/Auth";

/**
 * Shared bulk CSV/Excel import flow (upload -> poll status -> show
 * completed/failed summary -> optional error-row download / undo), cloned
 * from EmployeeInout.js's attendance import pattern so WFH/On Duty (and any
 * future module) reuse one implementation instead of forking it per screen.
 *
 * `urls` shape - mirrors the attendance-import / leave importFile route
 * conventions (see service/index.js):
 *   { upload, status, history, downloadErrors, undo, cancel, statistics }
 * `status`/`downloadErrors`/`undo`/`cancel` are treated as base paths the
 * session id is appended to (`${status}/${sessionId}`), matching
 * attendanceImportStatus/attendanceImportDownloadErrors's own usage.
 *
 * @param {Object} urls import endpoint base paths (see above)
 * @param {Function} [onImportComplete] called after a completed import (e.g. refresh the list)
 * @param {Function} [dispatch] redux dispatch, for the auth loader (optional)
 */
const useBulkImport = (urls, onImportComplete, dispatch) => {
  const [importSessionId, setImportSessionId] = useState(null);
  const [importProgressVisible, setImportProgressVisible] = useState(false);
  const [importStatus, setImportStatus] = useState(null);
  const [importPolling, setImportPolling] = useState(false);
  const [importHistoryVisible, setImportHistoryVisible] = useState(false);
  const importPollingRef = useRef(null);

  useEffect(() => {
    return () => {
      if (importPollingRef.current) {
        clearInterval(importPollingRef.current);
      }
    };
  }, []);

  // A clean import (no failed rows) auto-closes the progress modal, same as
  // EmployeeInout.js - failed rows keep it open so the user can download the
  // error report before dismissing it.
  useEffect(() => {
    if (importStatus?.status !== "completed" || importStatus?.failedRecords > 0) {
      return undefined;
    }
    const timer = setTimeout(() => setImportProgressVisible(false), 4000);
    return () => clearTimeout(timer);
  }, [importStatus]);

  const startImportPolling = (sessionId) => {
    setImportPolling(true);
    if (importPollingRef.current) {
      clearInterval(importPollingRef.current);
    }
    importPollingRef.current = setInterval(async () => {
      try {
        const response = await Service.makeAPICall({
          methodName: Service.getMethod,
          api_url: `${urls.status}/${sessionId}`,
        });
        if (response?.data?.status === 1) {
          const analytics = response.data.data;
          setImportStatus(analytics);
          if (["completed", "failed", "cancelled"].includes(analytics.status)) {
            clearInterval(importPollingRef.current);
            importPollingRef.current = null;
            setImportPolling(false);
            if (analytics.status === "completed") {
              onImportComplete?.();
            }
          }
        }
      } catch {
        clearInterval(importPollingRef.current);
        importPollingRef.current = null;
        setImportPolling(false);
      }
    }, 3000);
  };

  const uploadFile = async (file, extraFields = {}) => {
    if (!file) return;
    try {
      dispatch?.(showAuthLoader());
      const data = new FormData();
      data.append("file", file);
      Object.entries(extraFields).forEach(([key, value]) => {
        if (value !== undefined && value !== null) data.append(key, value);
      });

      const response = await Service.makeAPICall({
        methodName: Service.postMethod,
        api_url: urls.upload,
        body: data,
        options: { "content-type": "multipart/form-data" },
      });

      dispatch?.(hideAuthLoader());

      if (response?.data?.status === 1) {
        const { sessionId } = response.data.data;
        setImportSessionId(sessionId);
        setImportProgressVisible(true);
        setImportStatus(null);
        startImportPolling(sessionId);
      } else {
        message.error(response?.data?.message || "Import upload failed");
      }
    } catch (error) {
      dispatch?.(hideAuthLoader());
      message.error("Import failed. Please try again.");
    }
  };

  const downloadErrors = async () => {
    if (!importSessionId) return;
    try {
      const response = await Service.makeAPICall({
        methodName: Service.getMethod,
        api_url: `${urls.downloadErrors}/${importSessionId}`,
      });
      const payload = response?.data;
      if (payload?.status === 1 && payload.data?.content) {
        const downloadLink = document.createElement("a");
        downloadLink.href = `data:text/csv;base64,${payload.data.content}`;
        downloadLink.download = payload.data.fileName || `import-errors-${importSessionId}.csv`;
        downloadLink.style.display = "none";
        document.body.appendChild(downloadLink);
        downloadLink.click();
        downloadLink.remove();
      } else if (payload?.status === 1 && payload.data?.filePath) {
        window.open(`/${payload.data.filePath}`, "_blank");
      } else {
        message.error(payload?.message || "No error file available");
      }
    } catch {
      message.error("Failed to download error report");
    }
  };

  const cancelImport = async () => {
    if (!importSessionId) return;
    try {
      await Service.makeAPICall({
        methodName: Service.postMethod,
        api_url: `${urls.cancel}/${importSessionId}`,
      });
      if (importPollingRef.current) {
        clearInterval(importPollingRef.current);
        importPollingRef.current = null;
      }
      setImportPolling(false);
      setImportProgressVisible(false);
      message.info("Import cancelled");
    } catch {
      message.error("Failed to cancel import");
    }
  };

  const undoImport = async (sessionId) => {
    try {
      const response = await Service.makeAPICall({
        methodName: Service.postMethod,
        api_url: `${urls.undo}/${sessionId}`,
      });
      if (response?.data?.status === 1) {
        message.success(response.data.message || "Import undone");
        onImportComplete?.();
        return true;
      }
      message.error(response?.data?.message || "Failed to undo import");
      return false;
    } catch {
      message.error("Failed to undo import");
      return false;
    }
  };

  return {
    importSessionId,
    importProgressVisible,
    setImportProgressVisible,
    importStatus,
    importPolling,
    importHistoryVisible,
    setImportHistoryVisible,
    uploadFile,
    downloadErrors,
    cancelImport,
    undoImport,
  };
};

export default useBulkImport;
